import React, { useEffect, useState } from 'react'
import { motion, useMotionValue, useTransform, animate } from 'framer-motion'
import { useDrag } from '@use-gesture/react'
import { useTranslation } from 'react-i18next'
import { Crown, Flag, Gem, Info, User } from 'lucide-react'
import { type Candidate, type PartnerProfile, type ReportReason, reportUser } from '../lib/api'
import { hapticImpact } from '../lib/haptics'
import ProfileDetailSheet from './ProfileDetailSheet'
import PhotoNavGuide from './PhotoNavGuide'

const PHOTO_GUIDE_SEEN_KEY = 'swipify_seen_photo_guide'

const REPORT_REASON_KEYS: { value: ReportReason; labelKey: string }[] = [
  { value: 'spam', labelKey: 'reportReasons.spam' },
  { value: 'fake', labelKey: 'reportReasons.fake' },
  { value: 'inappropriate', labelKey: 'reportReasons.inappropriate' },
  { value: 'harassment', labelKey: 'reportReasons.harassment' },
]

interface SwipeCardProps {
  candidate: Candidate
  nextCandidate?: Candidate
  onSwipe: (action: 'like' | 'skip' | 'crush') => Promise<void>
  disabled: boolean
  /** programmaticAction triggers an imperative exit without drag */
  programmaticAction?: 'like' | 'skip' | 'crush' | null
  onProgrammaticDone?: () => void
}

export default function SwipeCard({
  candidate,
  nextCandidate,
  onSwipe,
  disabled,
  programmaticAction,
  onProgrammaticDone,
}: SwipeCardProps) {
  const { t } = useTranslation()
  const [showReportSheet, setShowReportSheet] = useState(false)
  const [reportDone, setReportDone] = useState(false)
  const [photoIndex, setPhotoIndex] = useState(0)
  const [showProfile, setShowProfile] = useState(false)
  const [showPhotoGuide, setShowPhotoGuide] = useState(
    () => candidate.photo_urls.length > 1 && localStorage.getItem(PHOTO_GUIDE_SEEN_KEY) !== '1',
  )

  function dismissPhotoGuide() {
    setShowPhotoGuide(false)
    localStorage.setItem(PHOTO_GUIDE_SEEN_KEY, '1')
  }

  async function handleReport(reason: ReportReason) {
    setShowReportSheet(false)
    await reportUser(candidate.user_id, reason).catch(() => {})
    hapticImpact('medium')
    setReportDone(true)
    setTimeout(() => setReportDone(false), 2000)
  }

  const x = useMotionValue(0)
  const rotate = useTransform(x, [-200, 0, 200], [-15, 0, 15])
  const likeOpacity = useTransform(x, [0, 120], [0, 1])
  const nopeOpacity = useTransform(x, [-120, 0], [1, 0])

  // Reset photo index when a new candidate is shown
  useEffect(() => { setPhotoIndex(0) }, [candidate.user_id])

  // Preload first photo of next candidate to avoid flicker when top card exits
  useEffect(() => {
    const url = nextCandidate?.photo_urls?.[0]
    if (url) {
      const img = new Image()
      img.src = url
    }
  }, [nextCandidate?.photo_urls])

  // Handle programmatic swipe from fallback buttons
  useEffect(() => {
    if (!programmaticAction) return
    const action = programmaticAction
    // Skip flies left; like and crush both fly right (crush reuses the
    // like-direction exit animation — a distinct visual is a deferred
    // follow-up, see docs/monetization-roadmap.md).
    const dir = action === 'skip' ? -1 : 1
    const target = dir * window.innerWidth * 1.3
    hapticImpact('medium')
    animate(x, target, { type: 'spring', stiffness: 200, damping: 30 }).then(() => {
      // Reset the deck's shared flag *before* invoking onSwipe. onSwipe is
      // async (awaits a network call before advancing to the next card), so
      // resetting it only after that promise settles left a window where
      // the next card could mount while this flag was still truthy —
      // causing its mount effect to immediately re-fire and auto-swipe.
      onProgrammaticDone?.()
      onSwipe(action)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmaticAction])

  const bind = useDrag(
    ({ active, movement: [mx], velocity: [vx], direction: [dx] }) => {
      if (disabled) return
      if (active) {
        x.set(mx)
        return
      }
      // Released — decide commit or spring back
      const threshold = window.innerWidth * 0.25
      const fast = Math.abs(vx) > 0.5
      if (mx > threshold || (fast && dx > 0)) {
        hapticImpact('medium')
        animate(x, window.innerWidth * 1.3, { type: 'spring', stiffness: 200, damping: 30 }).then(
          () => onSwipe('like'),
        )
      } else if (mx < -threshold || (fast && dx < 0)) {
        hapticImpact('medium')
        animate(x, -window.innerWidth * 1.3, {
          type: 'spring',
          stiffness: 200,
          damping: 30,
        }).then(() => onSwipe('skip'))
      } else {
        // Spring back to center
        animate(x, 0, { type: 'spring', stiffness: 300, damping: 25 })
      }
    },
    { filterTaps: true },
  )

  return (
    <motion.div
      {...(disabled || programmaticAction || showProfile ? {} : (bind() as object))}
      style={{ x, rotate, touchAction: 'none' } as unknown as React.CSSProperties}
      className="absolute inset-0 cursor-grab active:cursor-grabbing"
    >
      <div
        className={`relative h-full overflow-hidden rounded-2xl bg-tg-secondary-bg shadow-md ${
          candidate.crushed_you ? 'ring-4 ring-cyan-400' : ''
        }`}
      >
        {candidate.photo_urls.length > 0 ? (
          <img
            src={candidate.photo_urls[photoIndex]}
            alt={candidate.display_name}
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-tg-hint">
            <User className="h-16 w-16" />
          </div>
        )}

        {/* Crushed-you badge — Tinder Super-Like-style card marker. Identity
            is revealed only by this card appearing at all (same fields as
            any other candidate) — no separate reveal channel. See
            docs/monetization-roadmap.md. */}
        {candidate.crushed_you && (
          <div className="absolute left-4 top-3 z-10 flex items-center gap-1 rounded-full bg-cyan-500 px-3 py-1 text-xs font-semibold text-white shadow-md">
            <Gem className="h-3.5 w-3.5 fill-current" />
            {t('swipeCard.crushedYou', { name: candidate.display_name })}
          </div>
        )}

        {/* Dot indicator — only when multiple photos */}
        {candidate.photo_urls.length > 1 && (
          <div className="absolute left-0 right-0 top-3 flex justify-center gap-1">
            {candidate.photo_urls.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === photoIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/50'
                }`}
              />
            ))}
          </div>
        )}

        {/* Tap zones for photo cycling — sits above photo, below other controls */}
        {candidate.photo_urls.length > 1 && !showReportSheet && (
          <>
            <button
              className="absolute bottom-0 left-0 top-0 w-2/5 focus:outline-none"
              onClick={(e) => { e.stopPropagation(); setPhotoIndex((i) => Math.max(0, i - 1)) }}
              aria-label={t('swipeCard.previousPhoto')}
            />
            <button
              className="absolute bottom-0 right-0 top-0 w-3/5 focus:outline-none"
              onClick={(e) => { e.stopPropagation(); setPhotoIndex((i) => Math.min(candidate.photo_urls.length - 1, i + 1)) }}
              aria-label={t('swipeCard.nextPhoto')}
            />
          </>
        )}

        {/* One-time coachmark teaching the photo tap-zones above */}
        {showPhotoGuide && !showReportSheet && !showProfile && (
          <PhotoNavGuide onDismiss={dismissPhotoGuide} />
        )}

        {/* LIKE overlay */}
        <motion.div
          style={{ opacity: likeOpacity }}
          className="pointer-events-none absolute left-4 top-6 rotate-[-20deg] rounded-lg border-4 border-green-400 px-3 py-1"
        >
          <span className="text-2xl font-black text-green-400">LIKE</span>
        </motion.div>

        {/* NOPE overlay */}
        <motion.div
          style={{ opacity: nopeOpacity }}
          className="pointer-events-none absolute right-4 top-6 rotate-[20deg] rounded-lg border-4 border-red-400 px-3 py-1"
        >
          <span className="text-2xl font-black text-red-400">NOPE</span>
        </motion.div>

        {/* Info overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-5 py-4">
          <div className="flex items-end justify-between">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-xl font-bold text-white">
                {candidate.display_name}, {candidate.age}
                {candidate.crown && (
                  <Crown
                    className="h-4 w-4 shrink-0 fill-current text-amber-400"
                    aria-label={t('swipeCard.crownBadge')}
                  />
                )}
              </p>
              <p className="text-sm text-white/80">
                {t('common.kmAway', { km: candidate.distance_km < 1 ? '< 1' : candidate.distance_km.toFixed(1) })}
              </p>
              {candidate.bio && (
                <p className="mt-1 line-clamp-2 text-xs text-white/70">{candidate.bio}</p>
              )}
            </div>
            {/* Profile detail button */}
            <button
              onClick={(e) => { e.stopPropagation(); setShowProfile(true) }}
              className="ml-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm"
              aria-label={t('swipeCard.viewFullProfile')}
            >
              <Info className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Report button */}
        <button
          onClick={(e) => { e.stopPropagation(); setShowReportSheet(true) }}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/30 text-red-400 backdrop-blur-sm"
          aria-label={t('swipeCard.reportUser')}
        >
          <Flag className="h-4 w-4 fill-current" />
        </button>

        {/* Reported toast */}
        {reportDone && (
          <div className="absolute inset-x-4 top-12 flex items-center justify-center rounded-xl bg-black/70 py-2 text-sm text-white">
            {t('swipeCard.reportSubmitted')}
          </div>
        )}
      </div>

      {/* Report bottom sheet */}
      {showReportSheet && (
        <div
          className="absolute inset-0 z-10 flex flex-col justify-end rounded-2xl bg-black/50"
          onClick={() => setShowReportSheet(false)}
        >
          <div
            className="rounded-t-2xl bg-tg-bg px-4 pb-8 pt-4"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="mb-3 text-center text-sm font-semibold text-tg-text">
              {t('swipeCard.reportTitle', { name: candidate.display_name })}
            </p>
            <div className="flex flex-col gap-2">
              {REPORT_REASON_KEYS.map((r) => (
                <button
                  key={r.value}
                  onClick={() => handleReport(r.value)}
                  className="rounded-xl bg-tg-secondary-bg px-4 py-3 text-left text-sm text-tg-text active:opacity-70"
                >
                  {t(r.labelKey)}
                </button>
              ))}
              <button
                onClick={() => setShowReportSheet(false)}
                className="mt-1 rounded-xl bg-tg-secondary-bg px-4 py-3 text-center text-sm font-medium text-red-500 active:opacity-70"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Partner profile sheet */}
      {showProfile && (
        <ProfileDetailSheet
          profile={candidateToProfile(candidate)}
          onClose={() => setShowProfile(false)}
        />
      )}
    </motion.div>
  )
}

/** Maps a Candidate (swipe deck) to a PartnerProfile (profile sheet). */
function candidateToProfile(c: Candidate): PartnerProfile {
  return {
    user_id: c.user_id,
    display_name: c.display_name,
    age: c.age,
    bio: c.bio,
    city: c.city,
    distance_km: c.distance_km,
    interests: c.interests,
    education: c.education,
    height_cm: c.height_cm,
    photo_urls: c.photo_urls,
    crown: c.crown,
  }
}
